import { useQuery } from '@tanstack/react-query';

import { testConnection } from '@/lib/api/generated/api';
import type { ConnectionTestResult } from '@/lib/api/generated/model';

import { toConnectionError } from '../errors';
import { connectionKeys } from '../queryKeys';

/**
 * The live test of one saved connection, as a query rather than a mutation, so
 * the provider card and the secrets page's group header read the same result.
 * A connect or replace invalidates `connectionKeys.all`, which re-tests — that is
 * the spec's "saved, then tested": a rejected token warns on the card instead of
 * blocking the form.
 */
export function useConnectionHealth(
  workspaceId: string,
  connectionId: string | undefined,
) {
  return useQuery({
    queryKey: connectionKeys.health(workspaceId, connectionId ?? ''),
    queryFn: async (): Promise<ConnectionTestResult> => {
      if (connectionId === undefined) {
        throw new Error('No connection to test.');
      }
      const response = await testConnection(workspaceId, connectionId);
      if (response.status === 200) return response.data;
      throw toConnectionError(response.status, response.data);
    },
    enabled: connectionId !== undefined,
    staleTime: 5 * 60_000,
    retry: false,
  });
}
